import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Public photo gallery of a listing: a large main photo (whole picture shown,
 * portrait iPhone photos included) and thumbnails that are large enough to
 * recognise the vehicle — a scrollable column on the right on desktop, a
 * horizontal strip under the photo on phones and tablets. Thumbnails are
 * cropped (object-fit: cover), never distorted; the selected one has a
 * turquoise border. Arrows, thumbnails, swipe and the keyboard arrows all
 * change the main photo. Presentation only: the photo URLs are used as given.
 */
export default function ListingGallery({ images, alt }) {
  const { t } = useTranslation();
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const [index, setIndex] = useState(0);
  const deskRefs = useRef([]);
  const mobileRefs = useRef([]);
  const touch = useRef(null);
  const count = list.length;
  const current = count ? Math.min(index, count - 1) : 0;

  useEffect(() => { if (index >= count && count) setIndex(0); }, [count, index]);
  // Keep the selected thumbnail visible inside its own scroll area only (never scrolls the page).
  useEffect(() => {
    for (const el of [deskRefs.current[current], mobileRefs.current[current]]) {
      const box = el && el.parentElement;
      if (!el || !box || !box.offsetParent) continue;            // list hidden at this screen size
      if (box.scrollHeight > box.clientHeight) {
        const top = el.offsetTop - box.offsetTop;
        if (top < box.scrollTop || top + el.offsetHeight > box.scrollTop + box.clientHeight) {
          box.scrollTo({ top: Math.max(0, top - (box.clientHeight - el.offsetHeight) / 2), behavior: 'smooth' });
        }
      } else if (box.scrollWidth > box.clientWidth) {
        const left = el.offsetLeft - box.offsetLeft;
        if (left < box.scrollLeft || left + el.offsetWidth > box.scrollLeft + box.clientWidth) {
          box.scrollTo({ left: Math.max(0, left - (box.clientWidth - el.offsetWidth) / 2), behavior: 'smooth' });
        }
      }
    }
  }, [current]);

  if (!count) return null;
  const go = (delta) => setIndex((i) => (Math.min(i, count - 1) + delta + count) % count);
  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };
  const onTouchStart = (e) => { const p = e.touches[0]; touch.current = { x: p.clientX, y: p.clientY }; };
  const onTouchEnd = (e) => {
    const start = touch.current;
    touch.current = null;
    if (!start || count < 2) return;
    const p = e.changedTouches[0];
    const dx = p.clientX - start.x;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(p.clientY - start.y)) go(dx < 0 ? 1 : -1);
  };

  const thumb = (img, idx, sizeClass, refs, prefix) => (
    <button
      key={`${idx}-${img}`}
      ref={(el) => { refs.current[idx] = el; }}
      type="button"
      onClick={() => setIndex(idx)}
      aria-label={t('scooters.detail.showPhoto', { n: idx + 1, total: count })}
      aria-current={current === idx ? 'true' : undefined}
      className={`relative shrink-0 rounded-xl overflow-hidden bg-slate-100 border-2 transition-all ${sizeClass} ${
        current === idx ? 'border-accent shadow-md' : 'border-transparent opacity-80 hover:opacity-100'}`}
      data-testid={`${prefix}-${idx}`}
    >
      <img src={img} alt={`${alt} ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
    </button>
  );

  return (
    <div className="mb-8 min-w-0" data-testid="listing-gallery">
      <div className={`grid grid-cols-1 gap-3 ${count > 1 ? 'lg:grid-cols-[minmax(0,1fr)_200px] xl:grid-cols-[minmax(0,1fr)_224px]' : ''}`}>
        <div
          className="relative rounded-2xl overflow-hidden bg-slate-100 h-[300px] sm:h-[420px] lg:h-[500px] outline-none focus-visible:ring-2 focus-visible:ring-accent"
          data-testid="main-image"
          tabIndex={count > 1 ? 0 : -1}
          onKeyDown={onKeyDown}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <img src={list[current]} alt={alt} className="w-full h-full object-contain" draggable={false} />
          {count > 1 && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-all"
                data-testid="gallery-prev"
                aria-label={t('scooters.detail.prevPhoto')}
              >
                <ChevronLeft className="w-5 h-5 text-primary" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md hover:bg-white transition-all"
                data-testid="gallery-next"
                aria-label={t('scooters.detail.nextPhoto')}
              >
                <ChevronRight className="w-5 h-5 text-primary" />
              </button>
            </>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs rounded-full px-3 py-1" data-testid="gallery-counter">
            {current + 1} / {count}
          </div>
        </div>

        {count > 1 && (
          <>
            {/* Desktop: scrollable column of large thumbnails beside the photo. */}
            <div className="hidden lg:flex flex-col gap-3 h-[500px] overflow-y-auto overscroll-contain pr-1" data-testid="gallery-thumbs-desktop">
              {list.map((img, idx) => thumb(img, idx, 'w-full h-[112px]', deskRefs, 'thumbnail'))}
            </div>
            {/* Phones and tablets: horizontal strip under the photo (scrolls inside itself). */}
            <div className="lg:hidden flex gap-2 overflow-x-auto overscroll-x-contain snap-x pb-1" data-testid="gallery-thumbs-mobile">
              {list.map((img, idx) => thumb(img, idx, 'w-24 h-[72px] sm:w-28 sm:h-[84px] snap-start', mobileRefs, 'thumbnail-m'))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
